// src/avatars.ts
// A tiny pixel character maker. Every avatar is a 16x16 grid built by
// stacking layers (face, hair, hat...). Each character in a layer is a
// palette key; "." means "leave whatever is underneath".
//
// Palette keys:
//   o outline        w white         k pink       K deep pink
//   s/S skin/shade   h/H hair/shade  z/Z shirt    f/F critter body
//   y yellow  Y gold  b blue  B deep blue  g green  G dark green
//   v violet  V deep violet  r red  T brown  N dark gray  c cream

export type Layer = Record<number, string>; // row index -> 16-char row

// ---------- people ----------

const BASE: Layer = {
  2: "....oooooooo....",
  3: "...osssssssso...",
  4: "..osssssssssso..",
  5: "..osssssssssso..",
  6: "..osssssssssso..",
  7: "..osssssssssso..",
  8: "..osssssssssso..",
  9: "..osssssssssso..",
  10: "...osssssssso...",
  11: "....oSSSSSSo....",
  12: "....ozSSSSzo....",
  13: "..oozzzzzzzzoo..",
  14: ".ozzzzzzzzzzzzo.",
  15: ".ozzzzzzzzzzzzo.",
};

export const EXPRESSIONS: Record<string, { label: string; layer: Layer }> = {
  // Eyes are 2x2 with a white shine pixel, which reads as "cute".
  smile: {
    label: "Smile",
    layer: {
      6: "....ow....ow....",
      7: "....oo....oo....",
      8: "...kk......kk...",
      9: "......o..o......",
      10: ".......oo.......",
    },
  },
  grin: {
    label: "Big grin",
    layer: {
      6: "....ow....ow....",
      7: "....oo....oo....",
      8: "...kk......kk...",
      9: "......oooo......",
      10: ".......rr.......",
    },
  },
  smirk: {
    label: "Mischief",
    layer: {
      5: "....oo.....oo...",
      6: ".....ow.....ow..",
      7: ".....oo.....oo..",
      8: "...kk......kk...",
      9: ".........o......",
      10: "......ooo.......",
    },
  },
  sleepy: {
    label: "Content",
    layer: {
      6: "................",
      7: "....oo....oo....",
      8: "...kk......kk...",
      9: "......o..o......",
      10: ".......oo.......",
    },
  },
  tongue: {
    label: "Silly",
    layer: {
      6: "....ow....ow....",
      7: "....oo....oo....",
      8: "...kk......kk...",
      9: "......oooo......",
      10: ".......kk.......",
    },
  },
  wink: {
    label: "Wink",
    layer: {
      6: "....ow..........",
      7: "....oo....oo....",
      8: "...kk......kk...",
      9: "......o..o......",
      10: ".......oo.......",
    },
  },
  shy: {
    label: "Shy",
    layer: {
      6: "................",
      7: "....oo....oo....",
      8: "..kkkk....kkkk..",
      9: "................",
      10: ".......oo.......",
    },
  },
};

export const HAIRSTYLES: Record<string, { label: string; layer: Layer }> = {
  short: {
    label: "Short",
    layer: {
      1: "....oooooooo....",
      2: "...ohhhhhhhho...",
      3: "..ohhhhhhhhhho..",
      4: "..ohhhhhhhhhho..",
      5: "..ohhhhhssssso..",
      6: "...h........h...",
    },
  },
  buzz: {
    label: "Buzzed",
    layer: {
      3: "...oHHHHHHHHo...",
      4: "..oHHHHHHHHHHo..",
      5: "..oH........Ho..",
    },
  },
  shaggy: {
    label: "Shaggy",
    layer: {
      1: "...oooooooooo...",
      2: "..ohhhhhhhhhho..",
      3: ".ohhhhhhhhhhhho.",
      4: ".ohhhHhhhhHhhho.",
      5: ".ohhhhshhhshhho.",
      6: ".oh..........ho.",
      7: ".oh..........ho.",
    },
  },
  mohawk: {
    label: "Mohawk",
    layer: {
      0: "......oooo......",
      1: "......ohho......",
      2: "....oooHHooo....",
      3: "...o..oHHo..o...",
    },
  },
  bob: {
    label: "Bob",
    layer: {
      1: "....oooooooo....",
      2: "...ohhhhhhhho...",
      3: "..ohhhhhhhhhho..",
      4: ".ohhhhhhhhhhhho.",
      5: ".ohhhhhhhhhhhho.",
      6: ".ohh........hho.",
      7: ".ohh........hho.",
      8: ".ohh........hho.",
      9: ".ohhh......hhho.",
      10: "..ohho....ohho..",
      11: "...oo......oo...",
    },
  },
  long: {
    label: "Long",
    layer: {
      1: "....oooooooo....",
      2: "...ohhhhhhhho...",
      3: "..ohhhhhhhhhho..",
      4: ".ohhhhhhhhhhhho.",
      5: ".ohhhhsssshhhho.",
      6: ".ohh........hho.",
      7: ".ohh........hho.",
      8: ".ohh........hho.",
      9: ".ohh........hho.",
      10: ".ohhh......hhho.",
      11: ".ohhh......hhho.",
      12: ".ohhh......hhho.",
      13: ".ohhho....ohhho.",
      14: "..oho......oho..",
    },
  },
  curly: {
    label: "Curly",
    layer: {
      0: "...oooooooooo...",
      1: "..ohhHhhHhhHho..",
      2: ".ohHhhHhhHhhHho.",
      3: "ohhhHhhHhhHhhhho",
      4: "ohHhhhhhhhhhhHho",
      5: "ohhhsssssssshhho",
      6: "ohh..........hho",
      7: "ohh..........hho",
      8: "ohhh........hhho",
      9: ".ohh........hho.",
      10: "..oo........oo..",
    },
  },
  buns: {
    label: "Space buns",
    layer: {
      0: ".oo..........oo.",
      1: "ohho.oooooo.ohho",
      2: "ohhoohhhhhhoohho",
      3: ".oohhhhhhhhhhoo.",
      4: "..ohhhhhhhhhho..",
      5: "..ohssssssssho..",
    },
  },
  afro: {
    label: "Afro",
    layer: {
      0: "...oooooooooo...",
      1: "..ohhhHhhHhhho..",
      2: ".ohhHhhhhhhHhho.",
      3: "ohhhhhHhhHhhhhho",
      4: "ohHhhhhhhhhhhHho",
      5: "ohhhssssssssshho",
      6: "ohh..........hho",
      7: "ohH..........Hho",
      8: "ohhh........hhho",
      9: ".ohh........hho.",
      10: "..oo........oo..",
    },
  },
  locs: {
    label: "Locs",
    layer: {
      1: "....oooooooo....",
      2: "...ohhhhhhhho...",
      3: "..ohhHhhHhhHho..",
      4: ".ohhhhhhhhhhhho.",
      5: ".ohhhssssssshho.",
      6: "ohHo........oHho",
      7: "ohho........ohho",
      8: "ohHo........oHho",
      9: "ohho........ohho",
      10: "ohHo........oHho",
      11: "ohho........ohho",
      12: "ohHo........oHho",
      13: "ohho........ohho",
      14: ".oo..........oo.",
    },
  },
  braids: {
    label: "Braids",
    layer: {
      1: "....oooooooo....",
      2: "...ohHhHhHhHo...",
      3: "..ohHhHhHhHhHo..",
      4: ".ohHhHhHhHhHhHo.",
      5: ".ohssssssssssho.",
      6: ".oHo........oHo.",
      7: ".oho........oho.",
      8: ".oHo........oHo.",
      9: ".oho........oho.",
      10: ".oHo........oHo.",
      11: ".oho........oho.",
      12: ".oHo........oHo.",
      13: ".oyo........oyo.",
      14: "..o..........o..",
    },
  },
  none: { label: "Bald", layer: {} },
};

export const HATS: Record<string, { label: string; layer: Layer }> = {
  none: { label: "None", layer: {} },
  bow: {
    label: "Bow",
    layer: {
      0: "........oo.oo...",
      1: ".......okkokko..",
      2: ".......okKKKko..",
      3: "........oo.oo...",
    },
  },
  flowers: {
    label: "Flower crown",
    layer: {
      2: "..kKk.yYy.kKk...",
      3: "...g...g...g....",
    },
  },
  beanie: {
    label: "Beanie",
    layer: {
      0: ".......oo.......",
      1: "......owwo......",
      2: "....okkkkkko....",
      3: "...okkkkkkkko...",
      4: "..okkkkkkkkkko..",
      5: "..oKKKKKKKKKKo..",
    },
  },
  cap: {
    label: "Cap",
    layer: {
      1: "....oooooooo....",
      2: "...obbbbbbbbo...",
      3: "..obbbbbbbbbbo..",
      4: "..oBBBBBBBBBBBBo",
      5: "..oooooooooooooo",
    },
  },
  witch: {
    label: "Witch hat",
    layer: {
      0: "........oo......",
      1: ".......ovvo.....",
      2: "......ovvvvo....",
      3: ".....ovvvvvvo...",
      4: "....oyyyyyyyyo..",
      5: "oooooooooooooooo",
    },
  },
  headphones: {
    label: "Headphones",
    layer: {
      1: "...oNNNNNNNNo...",
      2: "..oN........No..",
      3: ".oN..........No.",
      6: "oBBo........oBBo",
      7: "oBBo........oBBo",
      8: "oBBo........oBBo",
    },
  },
  headwrap: {
    label: "Headwrap",
    layer: {
      0: "......oooo......",
      1: ".....oyrryo.....",
      2: "...ooyyrryyoo...",
      3: "..oyrryyrryyro..",
      4: "..oyyrryyrryyo..",
      5: "..oooooooooooo..",
    },
  },
  hijab: {
    label: "Hijab",
    layer: {
      0: "....oooooooo....",
      1: "...ovvvvvvvvo...",
      2: "..ovvvvvvvvvvo..",
      3: ".ovvvvvvvvvvvvo.",
      4: ".ovvvvvvvvvvvvo.",
      5: ".ovvvvvvvvvvvvo.",
      6: ".ovo........ovo.",
      7: ".ovo........ovo.",
      8: ".ovo........ovo.",
      9: ".ovvo......ovvo.",
      10: ".ovvvo....ovvvo.",
      11: ".ovvvvoooovvvvo.",
      12: ".ovvvvvvvvvvvvo.",
      13: "ovvvvvvvvvvvvvvo",
      14: "oVvvvvvvvvvvvvVo",
      15: "oVvvvvvvvvvvvvVo",
    },
  },
  horns: {
    label: "Little horns",
    layer: {
      0: "..o..........o..",
      1: "..oro......oro..",
      2: "...oro....oro...",
    },
  },
};

export const FACE_EXTRAS: Record<string, { label: string; layer: Layer }> = {
  none: { label: "None", layer: {} },
  glasses: {
    label: "Glasses",
    layer: {
      5: "...oooo..oooo...",
      6: "...o..oooo..o...",
      7: "...o..o..o..o...",
      8: "...oooo..oooo...",
    },
  },
  freckles: {
    label: "Freckles",
    layer: {
      8: "....T.T..T.T....",
      9: ".....T....T.....",
    },
  },
  beard: {
    label: "Beard",
    layer: {
      9: "..oh........ho..",
      10: "...ohhh..hhho...",
      11: "....ohhhhhho....",
    },
  },
  eyepatch: {
    label: "Eyepatch",
    layer: {
      5: "..oooooooooooo..",
      6: "....ooo.........",
      7: "....ooo.........",
    },
  },
  earrings: {
    label: "Earrings",
    layer: {
      9: "..y..........y..",
      10: "..Y..........Y..",
    },
  },
  bandaid: {
    label: "Bandaid",
    layer: {
      8: "..........ccc...",
      9: "..........ctc...",
    },
  },
};

// ---------- critters (whole characters; "f" is their body color) ----------

export const CRITTERS: Record<string, { label: string; layer: Layer }> = {
  cat: {
    label: "Cat",
    layer: {
      1: "..oo........oo..",
      2: "..okko....okko..",
      3: "..offfoooofffo..",
      4: ".offffffffffffo.",
      5: ".offffffffffffo.",
      6: ".offowffffowffo.",
      7: ".offooffffooffo.",
      8: ".ofkkffffffkkfo.",
      9: "oofffffkkfffffoo",
      10: ".offffoffoffffo.",
      11: "..offffooffffo..",
      12: "...offffffffo...",
      13: "..offffffffffo..",
      14: ".offffffffffffo.",
      15: ".offffffffffffo.",
    },
  },
  bunny: {
    label: "Bunny",
    layer: {
      0: "...oo......oo...",
      1: "..ofko....okfo..",
      2: "..ofko....okfo..",
      3: "..ofko....okfo..",
      4: "..offoooooooffo.",
      5: ".offffffffffffo.",
      6: ".offowffffowffo.",
      7: ".offooffffooffo.",
      8: ".ofkkffffffkkfo.",
      9: ".offfffkkfffffo.",
      10: ".offffoffoffffo.",
      11: "..offffooffffo..",
      12: "..offffffffffo..",
      13: ".offffffffffffo.",
      14: ".offffffffffffo.",
      15: ".offffffffffffo.",
    },
  },
  frog: {
    label: "Frog",
    layer: {
      1: "..oooo....oooo..",
      2: ".offffo..offffo.",
      3: ".ofowfoooofowfo.",
      4: ".ofooffffffoofo.",
      5: ".offffffffffffo.",
      6: ".ofkkffffffkkfo.",
      7: ".offoffffffoffo.",
      8: ".offfoooooofffo.",
      9: ".offffffffffffo.",
      10: "..offffffffffo..",
      11: "...oooooooooo...",
      12: "..offffffffffo..",
      13: ".offffffffffffo.",
      14: ".offffffffffffo.",
      15: ".offffffffffffo.",
    },
  },
  ghost: {
    label: "Ghost",
    layer: {
      1: ".....oooooo.....",
      2: "...ooffffffoo...",
      3: "..offffffffffo..",
      4: ".offffffffffffo.",
      5: ".offowffffowffo.",
      6: ".offooffffooffo.",
      7: ".ofkkffffffkkfo.",
      8: ".offffooooffffo.",
      9: ".offfffkkfffffo.",
      10: ".offffffffffffo.",
      11: ".offffffffffffo.",
      12: ".offffffffffffo.",
      13: ".offo.offo.offo.",
      14: "..oo...oo...oo..",
    },
  },
  imp: {
    label: "Imp",
    layer: {
      0: "..o..........o..",
      1: "..oro......oro..",
      2: "..orro.oo.orro..",
      3: "..offffffffffo..",
      4: ".offffffffffffo.",
      5: ".ofooffffffoofo.",
      6: ".offowffffowffo.",
      7: ".offooffffooffo.",
      8: ".ofkkffffffkkfo.",
      9: ".offfoooooofffo.",
      10: ".offffwffwffffo.",
      11: "..offffffffffo..",
      12: "...offffffffo...",
      13: "..offffffffffo..",
      14: ".offffffffffffo.",
      15: ".offffffffffffo.",
    },
  },
  mushroom: {
    label: "Mushroom",
    layer: {
      1: ".....oooooo.....",
      2: "...ooffwfffoo...",
      3: "..offwwffffffo..",
      4: ".offffffffwwffo.",
      5: ".offwffffffwffo.",
      6: "oooooooooooooooo",
      7: "...occcccccco...",
      8: "...ocowccowco...",
      9: "...ocooccooco...",
      10: "...okccooccko...",
      11: "...occcccccco...",
      12: "...occcccccco...",
      13: "...oooooooooo...",
    },
  },
  alien: {
    label: "Alien",
    layer: {
      0: "...y........y...",
      1: "....o......o....",
      2: ".....oooooo.....",
      3: "...ooffffffoo...",
      4: "..offffffffffo..",
      5: ".offffffffffffo.",
      6: ".offoooffoooffo.",
      7: ".offowoffowoffo.",
      8: ".offoooffoooffo.",
      9: ".ofkffffffffkfo.",
      10: "..offffooffffo..",
      11: "...offffffffo...",
      12: "....offffffo....",
      13: "...oooooooooo...",
    },
  },
};

// ---------- colors ----------

export const SKIN_TONES = [
  "#ffe0c7",
  "#f5c8a4",
  "#e0a77e",
  "#c68657",
  "#8d5a3b",
  "#5c3a28",
  "#b8e6c4", // mint
  "#d4c2f7", // lavender
  "#a9dcf5", // sky
  "#c9c9c9", // stone
  // deeper tones, added later (kept at the end so saved avatars don't change)
  "#a96d45",
  "#734830",
  "#4b2f20",
  "#3d2519",
];

// The order the skin swatches are shown in: light to deep, then the fantasy colors.
export const SKIN_ORDER = [0, 1, 2, 3, 10, 4, 11, 5, 12, 13, 6, 7, 8, 9];

export const HAIR_COLORS = [
  "#2b1d16",
  "#5c3a28",
  "#a0703c",
  "#e8c15a",
  "#c4432f",
  "#f5a9b8",
  "#5bcefa",
  "#b48ae6",
  "#6cc46c",
  "#e8e8e8",
];

export const BODY_COLORS = [
  "#f5a9b8",
  "#5bcefa",
  "#ffe74c",
  "#6cc46c",
  "#b48ae6",
  "#e04848",
  "#ff9f43",
  "#fffaf0",
  "#a0703c",
  "#4a3426",
];

const FIXED: Record<string, string> = {
  o: "#24170f",
  w: "#ffffff",
  c: "#fffaf0",
  k: "#f59ab0",
  K: "#c94f71",
  y: "#ffd23f",
  Y: "#d99a00",
  b: "#5bcefa",
  B: "#1f74a3",
  g: "#6cc46c",
  G: "#2f7d4f",
  v: "#b48ae6",
  V: "#7a4fc9",
  r: "#e04848",
  T: "#8a5c30",
  N: "#5c6066",
};

function shade(hex: string, amount = 0.18): string {
  const n = parseInt(hex.slice(1), 16);
  const ch = (shift: number) =>
    Math.max(0, Math.round(((n >> shift) & 255) * (1 - amount)));
  return `#${[ch(16), ch(8), ch(0)].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
}

// ---------- config ----------

export type AvatarConfig = {
  kind: "person" | "critter";
  skin: number;
  hair: string;
  hairColor: number;
  expression: string;
  hat: string;
  extra: string;
  shirt: number;
  critter: string;
  body: number;
};

export const DEFAULT_AVATAR: AvatarConfig = {
  kind: "person",
  skin: 1,
  hair: "short",
  hairColor: 1,
  expression: "smile",
  hat: "none",
  extra: "none",
  shirt: 1,
  critter: "cat",
  body: 7,
};

// Fill in anything missing or unknown so saved data can't break rendering.
export function normalizeAvatar(raw: unknown): AvatarConfig {
  const c = { ...DEFAULT_AVATAR, ...(typeof raw === "object" && raw ? raw : {}) };
  const pick = <T extends string>(v: string, table: Record<string, unknown>, d: T) =>
    (v in table ? v : d) as T;
  const idx = (v: number, list: unknown[], d: number) =>
    Number.isInteger(v) && v >= 0 && v < list.length ? v : d;
  return {
    kind: c.kind === "critter" ? "critter" : "person",
    skin: idx(c.skin, SKIN_TONES, DEFAULT_AVATAR.skin),
    hair: pick(c.hair, HAIRSTYLES, DEFAULT_AVATAR.hair),
    hairColor: idx(c.hairColor, HAIR_COLORS, DEFAULT_AVATAR.hairColor),
    expression: pick(c.expression, EXPRESSIONS, DEFAULT_AVATAR.expression),
    hat: pick(c.hat, HATS, DEFAULT_AVATAR.hat),
    extra: pick(c.extra, FACE_EXTRAS, DEFAULT_AVATAR.extra),
    shirt: idx(c.shirt, BODY_COLORS, DEFAULT_AVATAR.shirt),
    critter: pick(c.critter, CRITTERS, DEFAULT_AVATAR.critter),
    body: idx(c.body, BODY_COLORS, DEFAULT_AVATAR.body),
  };
}

// Stack the layers into a 16x16 grid of colors (null = transparent).
export function renderAvatar(config: AvatarConfig): (string | null)[][] {
  const grid: (string | null)[][] = Array.from({ length: 16 }, () =>
    Array<string | null>(16).fill(null)
  );

  const skin = SKIN_TONES[config.skin];
  const hair = HAIR_COLORS[config.hairColor];
  const shirt = BODY_COLORS[config.shirt];
  const body = BODY_COLORS[config.body];
  const palette: Record<string, string> = {
    ...FIXED,
    s: skin,
    S: shade(skin),
    h: hair,
    H: shade(hair, 0.25),
    z: shirt,
    Z: shade(shirt),
    f: body,
    F: shade(body),
  };

  const layers: Layer[] =
    config.kind === "critter"
      ? [CRITTERS[config.critter].layer, HATS[config.hat].layer]
      : [
          BASE,
          EXPRESSIONS[config.expression].layer,
          HAIRSTYLES[config.hair].layer,
          FACE_EXTRAS[config.extra].layer,
          HATS[config.hat].layer,
        ];

  for (const layer of layers) {
    for (const [row, line] of Object.entries(layer)) {
      const y = Number(row);
      for (let x = 0; x < 16; x++) {
        const key = line[x];
        if (key && key !== "." && palette[key]) grid[y][x] = palette[key];
      }
    }
  }
  return grid;
}
